import { useFormContext, useWatch } from 'react-hook-form';
import {
  Binding,
  BindingStatus,
  CreateTaskInput,
  TaskDirection,
  TaskSettlementStatus,
  TaskStatus,
} from '@admiin-com/ds-graphql';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ESignature from './ESignature';
import PageSelector from '../PageSelector/PageSelector';
import { useUserId } from '../../hooks/useCurrentUser/useCurrentUser';
import { ESignatureConfirm } from './ESignatureConfirm';
import { useTaskCreationContext } from '../../pages/TaskCreation';
import Onboarding from './Onboarding';
import { useSearchParams } from 'react-router-dom';
import { TaskCreateType } from '../../features/components/CreateTaskButtonGroup/CreateTaskButtonGroup';
import { useScanningTax } from './hooks/useScanningTax';
import { useTaskCreate } from '../../features/providers/TaskCreationProvider/useTaskCreate';
import TaxWorkflow from '../../features/components/CreateTask/TaxWorkflow/TaxWorkflow';
import { useCreateBindingTask } from '../../hooks/useCreateBindingTask';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  onComplete: (
    task: CreateTaskInput,
    options?: { binding?: Binding | null }
  ) => void;
  taskType?: TaskCreateType;
}

const ESignatureContainer = (props: Props) => {
  const [page, setPage] = React.useState<
    'Upload' | 'Signature' | 'Confirm' | 'TaxBinding'
  >('Upload');

  const { t } = useTranslation();

  const { control, setValue, watch } = useFormContext();
  const annotations = useWatch({ control, name: 'annotations' });
  const documents = useWatch({ control, name: 'documents' });

  const documentUrl = documents?.[0]?.src;
  const ref = React.useRef(null);
  const userId = useUserId();
  const { task, setIsTaxBinding, isTaxBinding } = useTaskCreationContext();
  const { createDocument: handleConfirm, saveDocument: handleDraft } =
    useTaskCreate({
      isTaxCreation: props.taskType === 'tax',
      taskType: 'esignature',
    });
  const [createBindingTask] = useCreateBindingTask();

  React.useEffect(() => {
    if ((task || documents?.length) && page === 'Upload') {
      // For DOCUMENT type, skip signature step and go directly to Confirm
      if (props.taskType === 'document') {
        setPage('Confirm');
      } else {
        setPage('Signature');
      }
    }

    if (task && task.bindingId && page !== 'TaxBinding') {
      setPage('TaxBinding');
    }
  }, [task, documents?.length, page, isTaxBinding, props.taskType]);

  const handleNext = async () => {
    try {
      if (isTaxBinding) {
        const values = watch();
        const existingBindingId = values.bindingId;
        const bindId = existingBindingId || uuidv4();
        if (!existingBindingId) {
          setValue('bindingId', bindId, {
            shouldDirty: false,
            shouldTouch: false,
          });
        }
        const result = await createBindingTask({
          tasks: [{ ...(values as any), bindingId: bindId }],
          sendTemplate: values.sendTemplate,
        });
        const createdTask = result?.data?.createBindingTask?.[0];
        if (createdTask) {
          const bindingRecord =
            result?.binding ??
            (createdTask.bindingId
              ? {
                  __typename: 'Binding' as const,
                  id: createdTask.bindingId,
                  status: result?.binding?.status ?? BindingStatus.SENT,
                  sendTemplate: values.sendTemplate ?? null,
                  emailMessage: values.emailMessage ?? null,
                }
              : null);
          props.onComplete(createdTask, {
            binding: bindingRecord,
          });
        }
      } else {
        const createdTask = await handleConfirm();
        if (createdTask) {
          props.onComplete(createdTask);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };
  const gotoDraft = async () => {
    try {
      const values = watch();
      let bindId = values.bindingId;
      if (isTaxBinding) {
        const existingBindingId = values.bindingId;
        bindId = existingBindingId || uuidv4();
        if (!existingBindingId) {
          setValue('bindingId', bindId, {
            shouldDirty: false,
            shouldTouch: false,
          });
        }
      }
      const createdTask = await handleDraft();
      if (createdTask) {
        props.onComplete(createdTask, {
          binding: isTaxBinding
            ? {
                __typename: 'Binding',
                id: createdTask?.bindingId ?? bindId!,
                status: BindingStatus.DRAFT,
                sendTemplate: values.sendTemplate ?? null,
                emailMessage: values.emailMessage ?? null,
              }
            : undefined,
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const gotoReview = async () => {
    try {
      const values = watch();
      let bindId = values.bindingId;
      if (isTaxBinding) {
        const existingBindingId = values.bindingId;
        bindId = existingBindingId || uuidv4();
        if (!existingBindingId) {
          setValue('bindingId', bindId, {
            shouldDirty: false,
            shouldTouch: false,
          });
        }
      }
      const createdTask = await handleDraft(undefined, TaskStatus.REVIEW);
      if (createdTask) {
        props.onComplete(createdTask, {
          binding: isTaxBinding
            ? {
                __typename: 'Binding',
                id: createdTask?.bindingId ?? bindId!,
                status: BindingStatus.REVIEW,
                sendTemplate: values.sendTemplate ?? null,
                emailMessage: values.emailMessage ?? null,
              }
            : undefined,
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();

  const handleChangeStepParams = (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('step', value);
    setSearchParams(params);
  };
  React.useEffect(() => {
    handleChangeStepParams(page.toString());
  }, [page]);

  const title = useMemo(() => {
    if (props.taskType === 'tax') return t('createTaxTitle', { ns: 'taskbox' });
    if (props.taskType === 'document')
      return t('createDocumentTitle', { ns: 'taskbox' });
    return t('eSignature', { ns: 'taskbox' });
  }, [props.taskType, t]);

  const description = useMemo(() => {
    if (props.taskType === 'tax')
      return t('taxLodgementMessageDescription', { ns: 'taskbox' });
    if (props.taskType === 'document')
      return t('documentMessageDescription', { ns: 'taskbox' });
    return t('esignatureMessageDescription', { ns: 'taskbox' });
  }, [props.taskType, t]);

  const {
    trigger: triggerScanning,
    data,
    loading,
    skippedKeys,
    handleSkipScanning,
    scanningKeys,
    onAssignEntity,
  } = useScanningTax();

  const [scannedDocumentKey, setScannedDocumentKey] = React.useState<
    string | null
  >(null);

  return (
    <PageSelector current={page}>
      <PageSelector.Page value={'Upload'}>
        <Onboarding
          isTax={props.taskType === 'tax'}
          onGetStarted={async (files, isBinding) => {
            setValue('documents', files, {
              shouldTouch: false,
              shouldDirty: false,
            });

            if (props.taskType === 'tax') {
              setValue('settlementStatus', TaskSettlementStatus.PAYABLE);
              for (const file of files) {
                if (file.src) {
                  triggerScanning(file.key, file.src);
                  setScannedDocumentKey(file.key);
                }
              }
            } else {
              setValue('reference', files[0].name ?? '', {
                shouldTouch: false,
                shouldDirty: false,
              });
            }

            if (files.length > 0)
              if (props.taskType === 'tax' && isBinding) {
                setValue('direction', TaskDirection.RECEIVING);
                setPage('TaxBinding');
                setIsTaxBinding(true);
              } else if (props.taskType === 'document') {
                // For DOCUMENT type, skip signature step and go directly to Confirm
                setPage('Confirm');
              } else {
                setPage('Signature');
              }
          }}
          title={title}
          description={description}
          buttonTitle={t('uploadADocument', { ns: 'taskbox' })}
        />
      </PageSelector.Page>
      <PageSelector.Page value={'TaxBinding'}>
        <TaxWorkflow
          documents={task?.bindingId ? [] : documents}
          onComplete={props.onComplete}
          scannedResults={data}
          scanningKeys={scanningKeys}
          skippedKeys={skippedKeys}
          onSkipScanning={(key: string) => {
            handleSkipScanning(key);
          }}
          onAssignEntity={onAssignEntity}
        />
      </PageSelector.Page>
      <PageSelector.Page value={'Signature'}>
        {documentUrl && (
          <ESignature
            ref={ref}
            documentUrl={documentUrl}
            userId={userId}
            annotations={annotations}
            handleDraft={async () => {
              setPage('Confirm');
            }}
            handleNext={async () => {
              setPage('Confirm');
            }}
          />
        )}
      </PageSelector.Page>
      <PageSelector.Page value={'Confirm'}>
        <ESignatureConfirm
          ref={ref}
          handleBackToEdit={() => {
            setPage('Signature');
          }}
          handleNext={handleNext}
          handleDraft={gotoDraft}
          handleSaveForReview={gotoReview}
          taxScanned={data.find(
            (bill) => bill.documentAnalysis?.fileKey === scannedDocumentKey
          )}
          taxScanning={loading}
          scanningSkipped={
            !!scannedDocumentKey && skippedKeys.has(scannedDocumentKey)
          }
          onSkipScanning={() => {
            handleSkipScanning(scannedDocumentKey);
          }}
        />
      </PageSelector.Page>
    </PageSelector>
  );
};
export default ESignatureContainer;
